import type { Permission } from '@fleetcore/shared'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { EmptyState } from '../components/empty-state'
import { useAuth } from './use-auth'
import { usePermissions } from './use-permissions'

export const LOGIN_PATH = '/login'

function FullPageLoader({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full w-full items-center justify-center gap-2 bg-background text-sm text-foreground-muted"
    >
      <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
      {label}
    </div>
  )
}

/**
 * Gate for everything behind authentication.
 *
 * `restoring` renders a loader rather than the login page: a reload with a valid
 * refresh token is authenticated, it just does not know it yet, and redirecting
 * during that window would throw away the deep link the user followed.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { t } = useTranslation()
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'restoring') return <FullPageLoader label={t('auth:session.restoring')} />

  if (status !== 'authenticated') {
    // The intended destination travels in history state, not in a query string:
    // it never reaches the server, cannot be logged by a proxy, and cannot be
    // used to bounce a user to an external origin after login.
    return <Navigate to={LOGIN_PATH} replace state={{ from: location }} />
  }

  return children ? <>{children}</> : <Outlet />
}

/** Keeps a signed-in user off the login page. */
export function RedirectIfAuthenticated({ to, children }: { to: string; children: ReactNode }) {
  const { status } = useAuth()
  if (status === 'authenticated') return <Navigate to={to} replace />
  return <>{children}</>
}

/**
 * Route-level permission check. Renders the "not allowed" state rather than
 * redirecting, because silently bouncing a user to the dashboard makes a
 * bookmarked link look broken instead of restricted.
 */
export function RequirePermission({
  anyOf,
  children,
}: {
  anyOf: readonly Permission[]
  children?: ReactNode
}) {
  const { t } = useTranslation()
  const { canAny } = usePermissions()

  if (!canAny(anyOf)) {
    return (
      <EmptyState
        title={t('errors:page.forbiddenTitle')}
        description={t('errors:page.forbiddenDescription')}
      />
    )
  }

  return children ? <>{children}</> : <Outlet />
}
